import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Ruler, ArrowLeftRight, Copy, Trash2 } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface ConversionCategory {
  name: string;
  units: { [key: string]: { name: string; factor: number } };
}

const UnitConverter: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('length');
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [result, setResult] = useState('');

  useSEO({
    title: 'Unit Converter - Convert Length, Weight, Temperature & More',
    description: 'Free online unit converter. Convert between different units of length, weight, temperature, speed, volume, and more with instant results.',
    keywords: 'unit converter, length converter, weight converter, temperature converter, metric converter, imperial converter',
    canonical: 'https://snap-tools.online/calc/unit-converter'
  });

  const categories: { [key: string]: ConversionCategory } = {
    length: {
      name: 'Length',
      units: {
        mm: { name: 'Millimeter', factor: 0.001 },
        cm: { name: 'Centimeter', factor: 0.01 },
        m: { name: 'Meter', factor: 1 },
        km: { name: 'Kilometer', factor: 1000 },
        in: { name: 'Inch', factor: 0.0254 },
        ft: { name: 'Foot', factor: 0.3048 },
        yd: { name: 'Yard', factor: 0.9144 },
        mi: { name: 'Mile', factor: 1609.344 }
      }
    },
    weight: {
      name: 'Weight',
      units: {
        mg: { name: 'Milligram', factor: 0.000001 },
        g: { name: 'Gram', factor: 0.001 },
        kg: { name: 'Kilogram', factor: 1 },
        oz: { name: 'Ounce', factor: 0.0283495 },
        lb: { name: 'Pound', factor: 0.453592 },
        stone: { name: 'Stone', factor: 6.35029 },
        ton: { name: 'Metric Ton', factor: 1000 }
      }
    },
    temperature: {
      name: 'Temperature',
      units: {
        c: { name: 'Celsius', factor: 1 },
        f: { name: 'Fahrenheit', factor: 1 },
        k: { name: 'Kelvin', factor: 1 }
      }
    },
    volume: {
      name: 'Volume',
      units: {
        ml: { name: 'Milliliter', factor: 0.001 },
        l: { name: 'Liter', factor: 1 },
        cup: { name: 'Cup (US)', factor: 0.236588 },
        pt: { name: 'Pint (US)', factor: 0.473176 },
        qt: { name: 'Quart (US)', factor: 0.946353 },
        gal: { name: 'Gallon (US)', factor: 3.78541 },
        floz: { name: 'Fluid Ounce (US)', factor: 0.0295735 }
      }
    },
    speed: {
      name: 'Speed',
      units: {
        'ms': { name: 'Meter/Second', factor: 1 },
        'kmh': { name: 'Kilometer/Hour', factor: 0.277778 },
        'mph': { name: 'Mile/Hour', factor: 0.44704 },
        'fps': { name: 'Foot/Second', factor: 0.3048 },
        'knot': { name: 'Knot', factor: 0.514444 }
      }
    }
  };

  // Initialize units when category changes
  useEffect(() => {
    const units = Object.keys(categories[selectedCategory].units);
    setFromUnit(units[0] || '');
    setToUnit(units[1] || units[0] || '');
    setResult('');
  }, [selectedCategory]);

  const convertTemperature = (value: number, from: string, to: string): number => {
    // Convert to Celsius first
    let celsius: number;
    switch (from) {
      case 'f':
        celsius = (value - 32) * 5/9;
        break;
      case 'k':
        celsius = value - 273.15;
        break;
      default:
        celsius = value;
    }

    // Convert from Celsius to target
    switch (to) {
      case 'f':
        return celsius * 9/5 + 32;
      case 'k':
        return celsius + 273.15;
      default:
        return celsius;
    }
  };

  const performConversion = () => {
    if (!inputValue || !fromUnit || !toUnit) {
      setResult('');
      return;
    }

    const value = parseFloat(inputValue);
    if (isNaN(value)) {
      setResult('Invalid input');
      return;
    }

    let convertedValue: number;

    if (selectedCategory === 'temperature') {
      convertedValue = convertTemperature(value, fromUnit, toUnit);
    } else {
      const category = categories[selectedCategory];
      const fromFactor = category.units[fromUnit].factor;
      const toFactor = category.units[toUnit].factor;
      
      // Convert to base unit, then to target unit
      const baseValue = value * fromFactor;
      convertedValue = baseValue / toFactor;
    }

    setResult(convertedValue.toString());
  };

  useEffect(() => {
    performConversion();
  }, [inputValue, fromUnit, toUnit, selectedCategory]);

  const copyResult = () => {
    if (result && result !== 'Invalid input') {
      navigator.clipboard.writeText(result);
      toast.success('Result copied to clipboard');
    }
  };

  const clearAll = () => {
    setInputValue('');
    setResult('');
    toast.success('Cleared all fields');
  };

  const swapUnits = () => {
    const temp = fromUnit;
    setFromUnit(toUnit);
    setToUnit(temp);
    
    if (result && result !== 'Invalid input') {
      setInputValue(result);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <Ruler className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            Unit Converter
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Convert between different units of measurement including length, weight, temperature, 
            volume, and speed with precise calculations.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        {/* Category Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-8 mb-6 sm:mb-8"
        >
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center">
            <Ruler className="h-5 w-5 mr-2 text-[#8438ff]" />
            Conversion Category
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {Object.entries(categories).map(([key, category]) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                  selectedCategory === key
                    ? 'border-[#8438ff] bg-[#8438ff]/5 text-[#8438ff]'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Conversion Interface */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
              <ArrowLeftRight className="h-5 w-5 mr-2 text-[#8438ff]" />
              Convert {categories[selectedCategory].name}
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={swapUnits}
                className="p-2 text-gray-500 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-all duration-200"
                title="Swap units"
              >
                <ArrowLeftRight className="h-4 w-4" />
              </button>
              <button
                onClick={clearAll}
                className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200"
                title="Clear all"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* From Unit */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">From</label>
              <div className="space-y-3">
                <select
                  value={fromUnit}
                  onChange={(e) => setFromUnit(e.target.value)}
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors"
                >
                  {Object.entries(categories[selectedCategory].units).map(([key, unit]) => (
                    <option key={key} value={key}>{unit.name}</option>
                  ))}
                </select>
                <input
                  type="number"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Enter value"
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors"
                />
              </div>
            </div>

            {/* To Unit */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">To</label>
              <div className="space-y-3">
                <select
                  value={toUnit}
                  onChange={(e) => setToUnit(e.target.value)}
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors"
                >
                  {Object.entries(categories[selectedCategory].units).map(([key, unit]) => (
                    <option key={key} value={key}>{unit.name}</option>
                  ))}
                </select>
                <div className="relative">
                  <input
                    type="text"
                    value={result}
                    readOnly
                    placeholder="Result will appear here"
                    className="w-full p-3 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-700"
                  />
                  {result && result !== 'Invalid input' && (
                    <button
                      onClick={copyResult}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 text-gray-500 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded transition-all duration-200"
                      title="Copy result"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Conversions */}
          {inputValue && result && result !== 'Invalid input' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 bg-gray-50 rounded-lg"
            >
              <h4 className="font-semibold text-gray-900 mb-2">Result</h4>
              <p className="text-sm text-gray-600">
                {inputValue} {categories[selectedCategory].units[fromUnit].name} = {' '}
                <span className="font-semibold text-[#8438ff]">
                  {parseFloat(result).toLocaleString()} {categories[selectedCategory].units[toUnit].name}
                </span>
              </p>
            </motion.div>
          )}
        </motion.div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8 sm:mt-12"
        />
      </div>
    </div>
  );
};

export default UnitConverter;
